import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaBan,
  FaUnlock,
  FaTrash,
  FaExternalLinkAlt,
  FaCopy,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaInbox,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTrophy,
  FaFire,
  FaUsers,
  FaChartLine,
  FaMoon,
  FaTh,
  FaList,
  FaCrown,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes clFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes clFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes clSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes clZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes clBar { from { width: 0; } }
@keyframes clRise { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
.cl-in { animation: clFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.cl-fade { animation: clFade .2s ease both; }
.cl-slide { animation: clSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.cl-zoom { animation: clZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.cl-bar { animation: clBar .8s cubic-bezier(.2,.7,.2,1) both; }
.cl-rise { animation: clRise .6s cubic-bezier(.2,.8,.2,1) both; }
.cl-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease, box-shadow .2s ease; }
.cl-press:hover { transform: translateY(-1px); }
.cl-press:active { transform: scale(.96); }
.cl-row { transition: background-color .15s ease; cursor: pointer; }
.cl-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.cl-card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px -14px rgba(15,23,42,.35); }
.cl-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .cl-in, .cl-fade, .cl-slide, .cl-zoom, .cl-bar, .cl-rise { animation: none; }
  .cl-press:hover, .cl-press:active, .cl-card:hover { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const karmaOf = (u) => Number(u?.point || 0);

const joinedAt = (u) => {
  if (!u?.reg_date) return null;
  const d = new Date(`${u.reg_date}T${u.reg_time || "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const isBlocked = (u) => String(u?.status || "").toLowerCase() === "block";
const isActive = (u) => String(u?.status || "").toLowerCase() === "live";

const waLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
};

// Karma levels (for quick filters and colour)
const LEVELS = [
  { id: "all", label: "All" },
  { id: "zero", label: "0 karma", test: (k) => k === 0 },
  { id: "low", label: "1 – 99", test: (k) => k >= 1 && k < 100 },
  { id: "mid", label: "100 – 499", test: (k) => k >= 100 && k < 500 },
  { id: "high", label: "500+", test: (k) => k >= 500 },
];

const levelColor = (k) =>
  k >= 500 ? "bg-violet-500" : k >= 100 ? "bg-cyan-500" : k > 0 ? "bg-emerald-500" : "bg-slate-300";

// Highlight matching search text
const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="cl-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 38, ring = "" }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return (
      <img
        src={src}
        alt=""
        onError={() => setErr(true)}
        className={`rounded-full object-cover shrink-0 bg-slate-100 ${ring}`}
        style={{ width: size, height: size }}
      />
    );
  return (
    <span
      className={`rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200 ${ring}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

const StatusBadge = ({ user }) => {
  if (isBlocked(user))
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Blocked
      </span>
    );
  if (isActive(user))
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
      </span>
    );
  return (
    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
      {user?.status || "Unknown"}
    </span>
  );
};

/* ================= page ================= */

const CreatorList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [searchIn, setSearchIn] = useState("all"); // all | name | user_id | email_id | contact
  const [level, setLevel] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ field: "karma", dir: "desc" });
  const [view, setView] = useState("table");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState([]);

  const [drawerUser, setDrawerUser] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type, users}
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same as before) ----- */

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "all-users");
      const res = await fetch(`${baseUrl}/users.php`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) && data?.error) {
        toast.current?.show({ severity: "error", summary: "Error", detail: data.error });
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch creators" });
    }
    setLoading(false);
  }, [baseUrl]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const setStatus = async (user_id, status) => {
    const formData = new FormData();
    formData.append("action", "block-user");
    formData.append("user_id", user_id);
    formData.append("status", status);
    const res = await fetch(`${baseUrl}/users.php`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok || (data && data.status === false)) throw new Error(data?.error || "Request failed");
    return data;
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, users: targets } = confirm;
    const status = type.includes("unblock") ? "Live" : type.includes("delete") ? "Delete" : "Block";
    setWorking(true);
    let ok = 0;
    for (const u of targets) {
      try {
        await setStatus(u.user_id, status);
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setDeleteText("");
    setSelectedIds([]);
    const verb = status === "Live" ? "unblocked" : status === "Block" ? "blocked" : "deleted";
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Done",
        detail: ok === 1 ? `Creator ${verb} successfully` : `${ok} creators ${verb} successfully`,
      });
    }
    if (ok < targets.length) {
      toast.current?.show({ severity: "error", summary: "Error", detail: `${targets.length - ok} could not be ${verb}` });
    }
    if (status === "Delete") setDrawerUser(null);
    fetchUsers();
  };

  const askToggle = (u) => setConfirm({ type: isBlocked(u) ? "unblock" : "block", users: [u] });
  const askDelete = (list) => {
    setDeleteText("");
    setConfirm({ type: list.length > 1 ? "bulk-delete" : "delete", users: list });
  };

  /* ----- derived ----- */

  // Rank by karma across all creators
  const rankMap = useMemo(() => {
    const m = {};
    [...users]
      .sort((a, b) => karmaOf(b) - karmaOf(a))
      .forEach((u, i) => {
        m[u.user_id] = i + 1;
      });
    return m;
  }, [users]);

  const maxKarma = useMemo(() => Math.max(1, ...users.map(karmaOf)), [users]);

  const stats = useMemo(() => {
    const total = users.length;
    const karma = users.reduce((s, u) => s + karmaOf(u), 0);
    return {
      total,
      karma,
      avg: total ? Math.round(karma / total) : 0,
      zero: users.filter((u) => karmaOf(u) === 0).length,
    };
  }, [users]);

  const podium = useMemo(
    () => [...users].filter((u) => karmaOf(u) > 0).sort((a, b) => karmaOf(b) - karmaOf(a)).slice(0, 3),
    [users]
  );

  const q = query.trim();

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const lvl = LEVELS.find((l) => l.id === level);
    let list = users.filter((u) => {
      if (lvl?.test && !lvl.test(karmaOf(u))) return false;
      if (statusFilter === "active" && !isActive(u)) return false;
      if (statusFilter === "blocked" && !isBlocked(u)) return false;
      if (!ql) return true;
      const fields = searchIn === "all" ? ["name", "user_id", "email_id", "contact"] : [searchIn];
      return fields.some((f) => String(u[f] ?? "").toLowerCase().includes(ql));
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let x;
      let y;
      if (sort.field === "karma") {
        x = karmaOf(a);
        y = karmaOf(b);
      } else if (sort.field === "joined") {
        x = joinedAt(a)?.getTime() || 0;
        y = joinedAt(b)?.getTime() || 0;
      } else {
        x = String(a[sort.field] || "").toLowerCase();
        y = String(b[sort.field] || "").toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
    return list;
  }, [users, q, searchIn, level, statusFilter, sort]);

  useEffect(() => setPage(1), [q, searchIn, level, statusFilter, pageSize, sort, view]);

  useEffect(() => {
    const valid = new Set(users.map((u) => u.user_id));
    setSelectedIds((ids) => ids.filter((id) => valid.has(id)));
    setDrawerUser((d) => (d ? users.find((u) => u.user_id === d.user_id) || null : d));
  }, [users]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageIds = pageRows.map((u) => u.user_id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const selectedUsers = users.filter((u) => selectedIds.includes(u.user_id));

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const togglePageSelect = () =>
    setSelectedIds((ids) =>
      allPageSelected ? ids.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...ids, ...pageIds]))
    );
  const selectAllResults = () => setSelectedIds(filtered.map((u) => u.user_id));

  const toggleSort = (field) =>
    setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  const hasFilters = q || level !== "all" || statusFilter !== "all";
  const clearFilters = () => {
    setQuery("");
    setLevel("all");
    setStatusFilter("all");
  };

  /* ----- misc ----- */

  const copyText = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = (list) => {
    const head = ["Rank", "User ID", "Name", "Karma", "Email", "Contact", "Gender", "DOB", "Status", "Reg. Date", "Reg. Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = list.map((u) =>
      [rankMap[u.user_id], u.user_id, u.name, karmaOf(u), u.email_id, u.contact, u.gender, u.dob, u.status, u.reg_date, u.reg_time]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-creators-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // "/" focuses search, Esc closes panels
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (e.key === "/" && !typing && !confirm && !drawerUser) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key !== "Escape") return;
      if (confirm && !working) setConfirm(null);
      else if (drawerUser) setDrawerUser(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, drawerUser]);

  /* ----- UI bits ----- */

  const statCards = [
    { label: "Creators", value: stats.total, icon: FaUsers, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300", onClick: () => setLevel("all") },
    { label: "Total karma", value: stats.karma, icon: FaFire, tint: "bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300", onClick: () => setSort({ field: "karma", dir: "desc" }) },
    { label: "Average karma", value: stats.avg, icon: FaChartLine, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setLevel("mid") },
    { label: "With 0 karma", value: stats.zero, icon: FaMoon, tint: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300", onClick: () => setLevel("zero") },
  ];

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button type="button" onClick={() => toggleSort(field)} className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white">
          {children}
          <Icon size={10} className={active ? "text-cyan-600" : "text-slate-300 dark:text-slate-500"} />
        </button>
      </th>
    );
  };

  const KarmaBar = ({ u }) => {
    const k = karmaOf(u);
    return (
      <div className="flex items-center gap-2 min-w-[120px]">
        <span className="w-12 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{fmt(k)}</span>
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
          <div className={`cl-bar h-full rounded-full ${levelColor(k)}`} style={{ width: `${Math.max(k ? 4 : 0, (k / maxKarma) * 100)}%` }} />
        </div>
      </div>
    );
  };

  const RankBadge = ({ rank }) => {
    const style =
      rank === 1
        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        : rank === 2
        ? "bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200"
        : rank === 3
        ? "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
        : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400";
    return (
      <span className={`inline-flex min-w-[28px] h-7 px-1.5 items-center justify-center rounded-md text-xs font-bold tabular-nums ${style}`}>
        {rank <= 3 ? <FaCrown size={11} /> : rank}
      </span>
    );
  };

  const confirmInfo = () => {
    if (!confirm) return {};
    const n = confirm.users.length;
    const who = n === 1 ? confirm.users[0].name || `creator ${confirm.users[0].user_id}` : `${n} creators`;
    if (confirm.type.includes("unblock"))
      return { title: `Unblock ${who}?`, body: "They can use the app again and will get an 'Account Activated' notification.", button: "Yes, unblock", color: "bg-emerald-600 hover:bg-emerald-700" };
    if (confirm.type.includes("delete"))
      return {
        title: `Delete ${who} permanently?`,
        body: "This removes the account and all its data: karma, wallet, posts, likes, comments, groups, withdrawals and reports. It cannot be undone.",
        button: n > 1 ? `Delete ${n} forever` : "Delete forever",
        color: "bg-rose-600 hover:bg-rose-700",
      };
    return { title: `Block ${who}?`, body: "They will not be able to use the app and will get an 'Account Suspended' notification.", button: "Yes, block", color: "bg-rose-600 hover:bg-rose-700" };
  };
  const ci = confirmInfo();
  const isDeleteConfirm = confirm?.type?.includes("delete");
  const deleteReady = !isDeleteConfirm || deleteText.trim().toUpperCase() === "DELETE";

  const RowActions = ({ u, compact }) => (
    <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => navigate(`/user-profile/${u.user_id}`)}
        title="Open full profile"
        aria-label="Open full profile"
        className="cl-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
      >
        <FaExternalLinkAlt size={11} />
      </button>
      <button
        type="button"
        onClick={() => askToggle(u)}
        title={isBlocked(u) ? "Unblock" : "Block"}
        className={`cl-press inline-flex items-center gap-1.5 h-8 ${compact ? "w-8 justify-center" : "px-3"} rounded-md text-sm font-medium whitespace-nowrap ${
          isBlocked(u)
            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
            : "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
        }`}
      >
        {isBlocked(u) ? <FaUnlock size={11} /> : <FaBan size={11} />}
        {!compact && (isBlocked(u) ? "Unblock" : "Block")}
      </button>
      <button
        type="button"
        onClick={() => askDelete([u])}
        title="Delete creator"
        aria-label="Delete creator"
        className="cl-press w-8 h-8 rounded-md bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white dark:bg-rose-900/30 dark:text-rose-300 flex items-center justify-center"
      >
        <FaTrash size={11} />
      </button>
    </div>
  );

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="cl-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">App Users</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Creator List</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Creators ranked by karma. Search anyone and manage their account.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportCsv(filtered)}
            disabled={!filtered.length}
            className="cl-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <FaFileCsv /> Export CSV
          </button>
          <button
            type="button"
            onClick={fetchUsers}
            disabled={loading}
            className="cl-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Big search */}
      <div className={`${CARD} cl-in p-4 sm:p-5`} style={{ animationDelay: "40ms" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search creators by name, user ID, email or phone number"
              className={`${INPUT} w-full h-12 pl-11 pr-24 text-base`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {query ? (
                <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={13} />
                </button>
              ) : (
                <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
              )}
            </div>
          </div>
          <select value={searchIn} onChange={(e) => setSearchIn(e.target.value)} className={`${INPUT} h-12 px-3 md:w-48`} aria-label="Search in">
            <option value="all">Search in: all fields</option>
            <option value="name">Name only</option>
            <option value="user_id">User ID only</option>
            <option value="email_id">Email only</option>
            <option value="contact">Phone only</option>
          </select>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs font-medium text-slate-500 mr-1">Karma:</span>
          {LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setLevel(l.id)}
              className={`cl-press h-8 px-3 rounded-full text-sm font-medium border ${
                level === l.id
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400"
              }`}
            >
              {l.label}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-200 dark:bg-slate-600 mx-1" />
          <span className="text-xs font-medium text-slate-500 mr-1">Status:</span>
          {[
            { id: "all", label: "All" },
            { id: "active", label: "Active" },
            { id: "blocked", label: "Blocked" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatusFilter(s.id)}
              className={`cl-press h-8 px-3 rounded-full text-sm font-medium border ${
                statusFilter === s.id
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400"
              }`}
            >
              {s.label}
            </button>
          ))}
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="ml-auto text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Podium + stats (hidden while searching to keep focus on results) */}
      {!q && (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 mt-4">
          <div className={`${CARD} cl-in p-5 xl:col-span-2`} style={{ animationDelay: "80ms" }}>
            <div className="flex items-center gap-2">
              <FaTrophy className="text-amber-500" />
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top creators</h2>
            </div>
            {podium.length ? (
              <div className="mt-4 grid grid-cols-3 items-end gap-2">
                {[podium[1], podium[0], podium[2]].map((u, i) => {
                  if (!u) return <div key={i} />;
                  const place = u === podium[0] ? 1 : u === podium[1] ? 2 : 3;
                  const h = place === 1 ? "h-24" : place === 2 ? "h-16" : "h-12";
                  const tone =
                    place === 1
                      ? "from-amber-300 to-amber-500 text-amber-950"
                      : place === 2
                      ? "from-slate-200 to-slate-400 text-slate-800"
                      : "from-orange-200 to-orange-400 text-orange-950";
                  return (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => setDrawerUser(u)}
                      className="cl-rise flex flex-col items-center text-center group"
                      style={{ animationDelay: `${place * 120}ms` }}
                    >
                      {place === 1 && <FaCrown className="text-amber-500 mb-1" />}
                      <Avatar src={u.profile_img} name={u.name} size={place === 1 ? 60 : 48} ring="ring-4 ring-white dark:ring-slate-800 group-hover:ring-cyan-200 transition" />
                      <p className="mt-1.5 text-sm font-semibold text-slate-900 dark:text-white truncate max-w-full">{u.name || "No name"}</p>
                      <p className="text-xs text-slate-500 tabular-nums">{fmt(karmaOf(u))} karma</p>
                      <div className={`mt-2 w-full ${h} rounded-t-lg bg-gradient-to-b ${tone} flex items-start justify-center pt-1.5 text-lg font-bold`}>
                        {place}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="mt-6 text-sm text-slate-500 text-center">No creator has karma yet</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 xl:col-span-3">
            {statCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.label}
                  type="button"
                  onClick={c.onClick}
                  className={`${CARD} cl-card cl-in p-4 flex items-center gap-4 text-left active:scale-[.98]`}
                  style={{ animationDelay: `${120 + i * 60}ms` }}
                >
                  <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                    <Icon />
                  </span>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                    {loading && !users.length ? (
                      <div className="h-6 w-12 mt-1 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
                    ) : (
                      <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Results */}
      <div className={`${CARD} cl-in mt-4`} style={{ animationDelay: "160ms" }}>
        {/* Results header */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <p className="text-sm text-slate-700 dark:text-slate-200 mr-auto">
            {q ? (
              <>
                <b>{fmt(filtered.length)}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
              </>
            ) : (
              <>
                <b>{fmt(filtered.length)}</b> of {fmt(users.length)} creators
              </>
            )}
          </p>
          {filtered.length > 0 && selectedIds.length < filtered.length && (
            <button type="button" onClick={selectAllResults} className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
              Select all {fmt(filtered.length)} results
            </button>
          )}
          <select
            value={`${sort.field}:${sort.dir}`}
            onChange={(e) => {
              const [field, dir] = e.target.value.split(":");
              setSort({ field, dir });
            }}
            className={`${INPUT} h-9 px-3`}
            aria-label="Sort"
          >
            <option value="karma:desc">Highest karma</option>
            <option value="karma:asc">Lowest karma</option>
            <option value="joined:desc">Newest joined</option>
            <option value="joined:asc">Oldest joined</option>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
          </select>
          <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {[
              { id: "table", icon: FaList, label: "Table view" },
              { id: "cards", icon: FaTh, label: "Card view" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setView(v.id)}
                  aria-label={v.label}
                  title={v.label}
                  className={`w-8 h-7 rounded-md flex items-center justify-center transition-all active:scale-95 ${
                    view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"
                  }`}
                >
                  <Icon size={12} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Bulk bar */}
        {selectedIds.length > 0 && (
          <div className="cl-fade flex flex-wrap items-center gap-2 px-4 py-2.5 bg-cyan-50 dark:bg-cyan-900/20 border-b border-cyan-200 dark:border-cyan-800 text-sm">
            <span className="font-medium text-cyan-900 dark:text-cyan-100 mr-2">{selectedIds.length} selected</span>
            <button
              type="button"
              onClick={() => askDelete(selectedUsers)}
              className="cl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium"
            >
              <FaTrash size={11} /> Delete
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ type: "bulk-block", users: selectedUsers.filter((u) => !isBlocked(u)) })}
              disabled={!selectedUsers.some((u) => !isBlocked(u))}
              className="cl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium disabled:opacity-40"
            >
              <FaBan size={11} /> Block
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ type: "bulk-unblock", users: selectedUsers.filter(isBlocked) })}
              disabled={!selectedUsers.some(isBlocked)}
              className="cl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-40"
            >
              <FaUnlock size={11} /> Unblock
            </button>
            <button
              type="button"
              onClick={() => exportCsv(selectedUsers)}
              className="cl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 font-medium"
            >
              <FaFileCsv size={11} /> Export
            </button>
            <button type="button" onClick={() => setSelectedIds([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
              Clear selection
            </button>
          </div>
        )}

        {/* Loading / empty */}
        {loading && !users.length ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 rounded-md bg-slate-100 dark:bg-slate-700 animate-pulse" />
            ))}
          </div>
        ) : pageRows.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <FaInbox className="mx-auto text-slate-300" size={32} />
            <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">
              {q ? `No creator found for "${q}"` : users.length ? "No creators match these filters" : "No creators found"}
            </p>
            {q && searchIn !== "all" && (
              <button type="button" onClick={() => setSearchIn("all")} className="mt-2 text-sm text-cyan-700 font-medium hover:underline">
                Search in all fields instead
              </button>
            )}
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="mt-2 ml-3 text-sm text-cyan-700 font-medium hover:underline">
                Clear all filters
              </button>
            )}
          </div>
        ) : view === "table" ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="pl-4 py-3 w-8">
                    <input type="checkbox" checked={allPageSelected} onChange={togglePageSelect} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select all on this page" />
                  </th>
                  <th className="px-3 py-3 font-medium text-left">Rank</th>
                  <SortTh field="name">Creator</SortTh>
                  <SortTh field="karma">Karma</SortTh>
                  <th className="px-3 py-3 font-medium text-left">Contact</th>
                  <th className="px-3 py-3 font-medium text-left">Gender</th>
                  <th className="px-3 py-3 font-medium text-left">DOB</th>
                  <SortTh field="joined">Joined</SortTh>
                  <th className="px-3 py-3 font-medium text-left">Status</th>
                  <th className="px-3 py-3 pr-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {pageRows.map((u) => {
                  const d = joinedAt(u);
                  const sel = selectedIds.includes(u.user_id);
                  return (
                    <tr
                      key={u.user_id}
                      onClick={() => setDrawerUser(u)}
                      className={`cl-row ${
                        drawerUser?.user_id === u.user_id
                          ? "bg-cyan-50/70 dark:bg-cyan-900/20"
                          : sel
                          ? "bg-slate-50 dark:bg-slate-800/40"
                          : "hover:bg-slate-50 dark:hover:bg-slate-700/40"
                      }`}
                    >
                      <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={sel} onChange={() => toggleSelect(u.user_id)} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label={`Select ${u.name}`} />
                      </td>
                      <td className="px-3 py-3">
                        <RankBadge rank={rankMap[u.user_id]} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3 min-w-[180px]">
                          <Avatar src={u.profile_img} name={u.name} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate max-w-[160px]">
                              <Hi text={u.name || "No name"} q={searchIn === "all" || searchIn === "name" ? q : ""} />
                            </p>
                            <p className="text-xs text-slate-500 tabular-nums">
                              <Hi text={u.user_id} q={searchIn === "all" || searchIn === "user_id" ? q : ""} />
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <KarmaBar u={u} />
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-slate-700 dark:text-slate-200 truncate max-w-[220px]" title={u.email_id}>
                          <Hi text={u.email_id || "-"} q={searchIn === "all" || searchIn === "email_id" ? q : ""} />
                        </p>
                        <p className="text-xs text-slate-500 tabular-nums">
                          <Hi text={u.contact || "-"} q={searchIn === "all" || searchIn === "contact" ? q : ""} />
                        </p>
                      </td>
                      <td className="px-3 py-3 text-slate-700 dark:text-slate-200">{u.gender || "-"}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-slate-700 dark:text-slate-200">{u.dob || "-"}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                        <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge user={u} />
                      </td>
                      <td className="px-3 py-3 pr-4">
                        <RowActions u={u} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 p-4">
            {pageRows.map((u, i) => {
              const sel = selectedIds.includes(u.user_id);
              return (
                <div
                  key={u.user_id}
                  onClick={() => setDrawerUser(u)}
                  className={`cl-card cl-in relative rounded-xl border p-4 cursor-pointer bg-white dark:bg-slate-800 ${
                    sel ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"
                  }`}
                  style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
                >
                  <div className="flex items-start justify-between">
                    <RankBadge rank={rankMap[u.user_id]} />
                    <input
                      type="checkbox"
                      checked={sel}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(u.user_id)}
                      className="w-4 h-4 accent-cyan-600 cursor-pointer"
                      aria-label={`Select ${u.name}`}
                    />
                  </div>
                  <div className="flex flex-col items-center text-center mt-1">
                    <Avatar src={u.profile_img} name={u.name} size={64} />
                    <p className="mt-2 font-semibold text-slate-900 dark:text-white truncate max-w-full">
                      <Hi text={u.name || "No name"} q={searchIn === "all" || searchIn === "name" ? q : ""} />
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      <Hi text={u.user_id} q={searchIn === "all" || searchIn === "user_id" ? q : ""} />
                    </p>
                    <div className="mt-2">
                      <StatusBadge user={u} />
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-700/40 px-3 py-2">
                    <p className="text-xs text-slate-500 mb-1">Karma</p>
                    <KarmaBar u={u} />
                  </div>
                  <div className="mt-3">
                    <RowActions u={u} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              Per page
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${INPUT} h-8 px-2`}>
                {[12, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-600 dark:text-slate-300 tabular-nums">
                {fmt((safePage - 1) * pageSize + 1)}–{fmt(Math.min(safePage * pageSize, filtered.length))} of {fmt(filtered.length)}
              </span>
              <div className="flex gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="cl-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                  <FaChevronLeft size={11} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="cl-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                  <FaChevronRight size={11} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ================= Drawer ================= */}
      {drawerUser && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="cl-fade absolute inset-0 bg-slate-900/40" onClick={() => setDrawerUser(null)} />
          <aside className="cl-slide relative w-full max-w-md h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-500">Creator details</p>
              <button type="button" onClick={() => setDrawerUser(null)} className="cl-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <div className="flex flex-col items-center text-center">
                <Avatar src={drawerUser.profile_img} name={drawerUser.name} size={84} />
                <h2 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">{drawerUser.name || "No name"}</h2>
                <button type="button" onClick={() => copyText(drawerUser.user_id, "User ID copied")} className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 tabular-nums">
                  ID {drawerUser.user_id} <FaCopy size={10} />
                </button>
                <div className="mt-2 flex items-center gap-2">
                  <StatusBadge user={drawerUser} />
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    Rank #{rankMap[drawerUser.user_id]} of {users.length}
                  </span>
                </div>
              </div>

              <div className="rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-100 dark:border-orange-900/40 p-4">
                <p className="flex items-center gap-1.5 text-xs font-medium text-orange-700 dark:text-orange-300">
                  <FaFire /> Karma
                </p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(karmaOf(drawerUser))}</p>
                <div className="mt-2 h-2 rounded-full bg-white/70 dark:bg-slate-700 overflow-hidden">
                  <div className={`cl-bar h-full rounded-full ${levelColor(karmaOf(drawerUser))}`} style={{ width: `${(karmaOf(drawerUser) / maxKarma) * 100}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">Compared with the top creator ({fmt(maxKarma)})</p>
              </div>

              <section className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                {[
                  { icon: FaEnvelope, label: "Email", value: drawerUser.email_id, href: drawerUser.email_id ? `mailto:${drawerUser.email_id}` : null },
                  { icon: FaPhoneAlt, label: "Contact", value: drawerUser.contact, href: drawerUser.contact ? `tel:${drawerUser.contact}` : null },
                ].map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.label} className="flex items-center gap-3 px-4 py-3">
                      <Icon className="text-slate-400 shrink-0" size={13} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500">{r.label}</p>
                        {r.href ? (
                          <a href={r.href} className="text-sm text-slate-900 dark:text-white hover:underline break-all">{r.value}</a>
                        ) : (
                          <p className="text-sm text-slate-400">-</p>
                        )}
                      </div>
                      {r.value && (
                        <button type="button" onClick={() => copyText(r.value)} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label={`Copy ${r.label}`}>
                          <FaCopy size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
                <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-700">
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500">Gender</p>
                    <p className="text-sm text-slate-900 dark:text-white">{drawerUser.gender || "-"}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500">DOB</p>
                    <p className="text-sm text-slate-900 dark:text-white">{drawerUser.dob || "-"}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs text-slate-500">Joined</p>
                    <p className="text-sm text-slate-900 dark:text-white">{fmtDate(joinedAt(drawerUser))}</p>
                  </div>
                </div>
              </section>

              {waLink(drawerUser.contact) && (
                <a href={waLink(drawerUser.contact)} target="_blank" rel="noreferrer" className="cl-press flex items-center justify-center gap-2 h-10 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-sm font-semibold">
                  <FaWhatsapp size={16} /> Message on WhatsApp
                </a>
              )}
              <button
                type="button"
                onClick={() => navigate(`/user-profile/${drawerUser.user_id}`)}
                className="cl-press w-full flex items-center justify-center gap-2 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <FaExternalLinkAlt size={11} /> Open full profile
              </button>
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <button type="button" onClick={() => askDelete([drawerUser])} className="cl-press h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-2">
                <FaTrash size={11} /> Delete
              </button>
              <button
                type="button"
                onClick={() => askToggle(drawerUser)}
                className={`cl-press flex-1 h-10 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 border ${
                  isBlocked(drawerUser)
                    ? "border-emerald-200 text-emerald-700 bg-white dark:bg-secondary-dark-bg hover:bg-emerald-50"
                    : "border-amber-200 text-amber-700 bg-white dark:bg-secondary-dark-bg hover:bg-amber-50"
                }`}
              >
                {isBlocked(drawerUser) ? <FaUnlock size={12} /> : <FaBan size={12} />}
                {isBlocked(drawerUser) ? "Unblock" : "Block"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.users.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="cl-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="cl-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-11 h-11 rounded-full flex items-center justify-center ${confirm.type.includes("unblock") ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {isDeleteConfirm ? <FaExclamationTriangle size={18} /> : confirm.type.includes("unblock") ? <FaUnlock size={16} /> : <FaBan size={16} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{ci.title}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{ci.body}</p>

            <div className="mt-4 max-h-40 overflow-y-auto rounded-lg bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.users.slice(0, 20).map((u) => (
                <div key={u.user_id} className="flex items-center gap-3 p-2.5">
                  <Avatar src={u.profile_img} name={u.name} size={30} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{u.name || "No name"}</p>
                    <p className="text-xs text-slate-500 tabular-nums">{u.user_id}</p>
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums">{fmt(karmaOf(u))} karma</span>
                </div>
              ))}
              {confirm.users.length > 20 && <p className="p-2.5 text-xs text-slate-500">and {confirm.users.length - 20} more</p>}
            </div>

            {isDeleteConfirm && (
              <div className="mt-4">
                <label htmlFor="cl-delete" className="text-sm text-slate-700 dark:text-slate-200">
                  Type <b>DELETE</b> to confirm
                </label>
                <input id="cl-delete" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className={`${INPUT} w-full h-10 px-3 mt-1.5`} />
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="cl-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working || !deleteReady}
                className={`cl-press flex-1 h-10 rounded-lg text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${ci.color}`}
              >
                {working && <FaSyncAlt className="animate-spin" size={12} />}
                {working ? "Please wait..." : ci.button}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorList;